// src/components/Footer.tsx
import React, { useState } from 'react';
import { FaFacebook, FaLinkedin, FaTiktok, FaMapMarkedAlt, FaPlusCircle, FaEnvelope, FaTimes, FaUser, FaTag, FaComment, FaExternalLinkAlt, FaGift, FaHome, FaQuestionCircle, FaShieldAlt, FaShoppingBag, FaLock, FaArrowRight, FaCalendarAlt, FaMagic, FaCommentDots } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import ExternalForfeoRedirectModal from './ExternalForfeoRedirectModal';
import { FORFEO_GIFT_CARD_URL, FORFEO_COM_URL } from '../lib/forfeoExternalLinks';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // --- ÉTATS INFOLETTRE ---
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [publicEmailWebsite, setPublicEmailWebsite] = useState('');
  const [newsletterPostalCode, setNewsletterPostalCode] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // --- ÉTATS MODAL DE CONTACT ---
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [contactData, setContactData] = useState({ name: '', email: '', subject: '', message: '' });
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [contactMessage, setContactMessage] = useState('');
  const [isForfeoComRedirectOpen, setIsForfeoComRedirectOpen] = useState(false);

  const TRANSACTIONAL_URL =
    (import.meta as any)?.env?.VITE_FORFEO_TRANSACTIONAL_URL || FORFEO_COM_URL || 'https://forfeo.com';

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const normalizePostalCode = (value: string) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length <= 3) return cleaned;
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)}`.trim();
  };

  const isValidCanadianPostalCode = (postal: string) => {
    const normalized = postal.toUpperCase().trim();
    return /^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/.test(normalized);
  };

  // --- SOUMISSION INFOLETTRE ---
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterMessage(null);

    const email = newsletterEmail.trim().toLowerCase();
    const postalCode = normalizePostalCode(newsletterPostalCode.trim());

    if (!email || !email.includes('@')) {
      setNewsletterMessage({ type: 'error', text: 'Veuillez entrer une adresse courriel valide.' });
      return;
    }

    if (!postalCode || !isValidCanadianPostalCode(postalCode)) {
      setNewsletterMessage({ type: 'error', text: 'Veuillez entrer un code postal valide (ex: G1K 7P4).' });
      return;
    }

    setNewsletterLoading(true);

    try {
      const { data: existing, error: checkError } = await supabase
        .from('newsletter_subscribers')
        .select('id, unsubscribe_token')
        .eq('email', email)
        .maybeSingle();

      if (checkError) throw checkError;

      let finalToken = existing?.unsubscribe_token;

      if (existing?.id) {
        const { error: updateError } = await supabase
          .from('newsletter_subscribers')
          .update({
            postal_code: postalCode,
            is_subscribed: true,
            unsubscribed_at: null,
          })
          .eq('id', existing.id);

        if (updateError) throw updateError;
        if (!finalToken) finalToken = 're_sub_' + Date.now();
      } else {
        const { data: newSub, error: insertError } = await supabase
          .from('newsletter_subscribers')
          .insert([{ email, postal_code: postalCode, is_subscribed: true }])
          .select('unsubscribe_token')
          .single();

        if (insertError) throw insertError;
        finalToken = newSub?.unsubscribe_token || 'new_' + Date.now();
      }

      await supabase.functions.invoke('send-newsletter-welcome-index-ts', {
        body: {
          email: email,
          postalCode: postalCode,
          unsubscribeToken: finalToken,
          website: publicEmailWebsite,
        },
      });

      const deliverabilityTip =
        'Conseil : si vous ne voyez pas notre courriel, vérifiez aussi vos indésirables (Spam/Promotions) ' +
        'et ajoutez forfeo2005@gmail.com à vos contacts.';

      setNewsletterMessage({
        type: 'success',
        text: existing
          ? `Votre inscription a été mise à jour. Merci 💙 ${deliverabilityTip}`
          : `Merci ! Vous êtes maintenant abonné(e) à l’infolettre Forfeo. ${deliverabilityTip}`,
      });

      setNewsletterEmail('');
      setNewsletterPostalCode('');
      setPublicEmailWebsite('');
    } catch (error: any) {
      console.error('Erreur inscription:', error);
      setNewsletterMessage({
        type: 'error',
        text: error?.message || 'Une erreur est survenue. Veuillez réessayer.',
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  // --- SOUMISSION CONTACT ---
  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactStatus('loading');
    setContactMessage('');

    try {
      const { error } = await supabase.functions.invoke('send-contact-email-index-ts', {
        body: {
          ...contactData,
          website: publicEmailWebsite,
        },
      });

      if (error) throw error;

      setContactStatus('success');
      setContactMessage(`Merci ! Votre message a été envoyé avec succès. Un membre de l'équipe Forfeo Explore vous contactera d'ici 24 à 72 heures.`);
      setContactData({ name: '', email: '', subject: '', message: '' });
      setPublicEmailWebsite('');
      
      setTimeout(() => {
        setIsContactOpen(false);
        setContactStatus('idle');
      }, 10000);
    } catch (error: any) {
      console.error('Erreur envoi contact:', error);
      setContactStatus('error');
      setContactMessage('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.');
    }
  };

  return (
    <>
      <ExternalForfeoRedirectModal
        isOpen={isForfeoComRedirectOpen}
        onClose={() => setIsForfeoComRedirectOpen(false)}
        targetUrl={FORFEO_GIFT_CARD_URL}
        contextLabel="forfeo.com/carte-cadeau"
      />
      <section className="relative overflow-hidden bg-[#07111f] text-white border-t border-white/10">
        <div className="absolute inset-0 opacity-[0.07] [background-image:linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] [background-size:28px_28px]" />
        <div className="absolute -left-32 top-0 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute right-0 bottom-0 h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="relative container mx-auto px-4 py-14">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.75fr_0.75fr_0.95fr]">
            <div>
              <div className="inline-flex items-center gap-3 rounded-3xl border border-white/10 bg-white/5 px-4 py-3 shadow-2xl backdrop-blur-md">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white"><FaMapMarkedAlt /></div>
                <div>
                  <p className="text-xl font-black tracking-tight">FORFEO <span className="text-cyan-300">EXPLORE</span></p>
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">La porte d’entrée numérique du Québec</p>
                </div>
              </div>
              <p className="mt-6 max-w-sm text-sm font-semibold leading-relaxed text-slate-300">
                Découvrez le Québec, trouvez des expériences locales et préparez vos moments à offrir. L’achat de la carte virtuelle Forfeo se fait sur forfeo.com, la vitrine transactionnelle officielle.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {['100% local', 'Vouchers numériques', 'Expériences locales', 'Québec & régions'].map((badge) => (
                  <span key={badge} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-slate-300">{badge}</span>
                ))}
              </div>
              <div className="mt-8 flex gap-3">
                <a href="https://www.facebook.com/people/Forfeo-Qu%C3%A9bec/61581227661449/" target="_blank" rel="noopener noreferrer" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition hover:bg-white hover:text-blue-600"><FaFacebook /></a>
                <a href="https://www.linkedin.com/in/forfeo-inc-468326385/" target="_blank" rel="noopener noreferrer" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition hover:bg-white hover:text-blue-700"><FaLinkedin /></a>
                <a href="https://www.tiktok.com/@forfeoinc" target="_blank" rel="noopener noreferrer" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition hover:bg-white hover:text-slate-950"><FaTiktok /></a>
              </div>
            </div>

            <div>
              <h4 className="mb-5 text-[11px] font-black uppercase tracking-[0.25em] text-blue-300">Navigation</h4>
              <ul className="space-y-3 text-sm font-bold text-slate-300">
                <li><Link to="/" onClick={scrollToTop} className="hover:text-white transition"><FaHome className="inline mr-2 text-slate-500" />Accueil</Link></li>
                <li><Link to="/decouvrir-quebec" onClick={scrollToTop} className="hover:text-white transition"><FaMapMarkedAlt className="inline mr-2 text-slate-500" />Découvrir le Québec</Link></li>
                <li><Link to="/alertes-intelligentes" onClick={scrollToTop} className="hover:text-white transition"><FaEnvelope className="inline mr-2 text-slate-500" />Alertes intelligentes</Link></li>
                <li><Link to="/mes-alertes-intelligentes" onClick={scrollToTop} className="hover:text-white transition"><FaEnvelope className="inline mr-2 text-slate-500" />Mes alertes</Link></li>
                <li><Link to="/boutique" onClick={scrollToTop} className="hover:text-white transition"><FaShoppingBag className="inline mr-2 text-slate-500" />Explorer les offres</Link></li>
                <li><Link to="/carte" onClick={scrollToTop} className="hover:text-white transition"><FaMapMarkedAlt className="inline mr-2 text-slate-500" />Carte interactive</Link></li>
                <li><Link to="/aide" onClick={scrollToTop} className="hover:text-white transition"><FaQuestionCircle className="inline mr-2 text-slate-500" />FAQ & assistance</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="mb-5 text-[11px] font-black uppercase tracking-[0.25em] text-cyan-300">Offrir</h4>
              <ul className="space-y-3 text-sm font-bold text-slate-300">
                <li><Link to="/offrir-un-moment" onClick={scrollToTop} className="hover:text-white transition"><FaGift className="inline mr-2 text-pink-300" />Offrir un moment</Link></li>
                <li><Link to="/calendrier-des-occasions" onClick={scrollToTop} className="hover:text-white transition"><FaCalendarAlt className="inline mr-2 text-violet-300" />Calendrier des occasions</Link></li>
                <li><Link to="/revelation-surprise" onClick={scrollToTop} className="hover:text-white transition"><FaMagic className="inline mr-2 text-fuchsia-300" />Révélation surprise</Link></li>
                <li><Link to="/avis-experience" onClick={scrollToTop} className="hover:text-white transition"><FaCommentDots className="inline mr-2 text-emerald-300" />Avis après expérience</Link></li>
                <li><Link to="/carte-cadeau" onClick={scrollToTop} className="hover:text-white transition"><FaGift className="inline mr-2 text-pink-300" />Carte Forfeo</Link></li>
                <li><button type="button" onClick={() => setIsForfeoComRedirectOpen(true)} className="hover:text-white transition text-left"><FaExternalLinkAlt className="inline mr-2 text-blue-300" />Acheter sur forfeo.com</button></li>
                <li><Link to="/decouvrir-quebec" onClick={scrollToTop} className="hover:text-white transition"><FaMapMarkedAlt className="inline mr-2 text-slate-500" />Idées cadeaux Québec</Link></li>
              </ul>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-md">
              <h4 className="mb-3 text-[11px] font-black uppercase tracking-[0.25em] text-cyan-300">Achat sécurisé</h4>
              <p className="text-sm font-semibold leading-relaxed text-slate-300">Les cartes virtuelles Forfeo s’achètent sur forfeo.com. Vous serez averti avant de quitter Forfeo Explore.</p>
              <button type="button" onClick={() => setIsForfeoComRedirectOpen(true)} className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-50">Acheter la carte <FaArrowRight /></button>
              <div className="mt-5 border-t border-white/10 pt-5">
                <p className="text-[11px] font-black uppercase tracking-widest text-slate-400"><FaLock className="inline mr-2" />Juridique & sécurité</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to="/privacy" className="rounded-full bg-white/5 px-3 py-1.5 text-[10px] font-bold text-slate-300 hover:text-white">Confidentialité</Link>
                  <Link to="/terms" className="rounded-full bg-white/5 px-3 py-1.5 text-[10px] font-bold text-slate-300 hover:text-white">Conditions</Link>
                  <Link to="/confiance" className="rounded-full bg-white/5 px-3 py-1.5 text-[10px] font-bold text-slate-300 hover:text-white"><FaShieldAlt className="inline mr-1" />Loi 25</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <footer className="bg-white text-navy py-16 font-sans border-t border-slate-200">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2 space-y-6">
              <div className="flex items-center gap-3 text-navy">
                <div className="bg-navy p-2 rounded-lg shadow-md">
                  <FaMapMarkedAlt className="text-white text-xl" />
                </div>
                <span className="text-xl font-black uppercase tracking-tighter">
                  Forfeo <span className="text-blue-600 font-light">Explore Québec</span>
                </span>
              </div>

              <p className="text-slate-500 text-sm leading-relaxed max-w-sm">
                Votre compagnon numérique intelligent pour découvrir les trésors cachés de la Belle Province. Une technologie propulsée par l&apos;IA au service du tourisme local.
              </p>

              {/* --- NOUVELLE SECTION RÉSEAUX SOCIAUX AVEC EFFET "WOW" --- */}
              <div className="pt-2">
                <div className="flex items-center gap-2 mb-4">
                  {/* Point clignotant moderne (Effet Live/Notification) */}
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-600"></span>
                  </span>
                  <p className="text-[11px] font-black text-navy uppercase tracking-widest">
                    Suivez notre communauté
                  </p>
                </div>
                
                <div className="flex space-x-4">
                  {/* Bouton Facebook avec effet de halo interactif */}
                  <a href="https://www.facebook.com/people/Forfeo-Qu%C3%A9bec/61581227661449/" target="_blank" rel="noopener noreferrer" 
                     className="relative flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 text-navy border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-500 hover:text-blue-600 transition-all duration-300 hover:-translate-y-1 group overflow-hidden">
                    <span className="absolute inset-0 rounded-full bg-blue-500/10 scale-0 group-hover:scale-150 transition-transform duration-500 ease-out"></span>
                    <FaFacebook size={20} className="relative z-10" />
                  </a>
                  
                  {/* Bouton LinkedIn */}
                  <a href="https://www.linkedin.com/in/forfeo-inc-468326385/" target="_blank" rel="noopener noreferrer" 
                     className="relative flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 text-navy border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-700 hover:text-blue-700 transition-all duration-300 hover:-translate-y-1 group overflow-hidden">
                    <span className="absolute inset-0 rounded-full bg-blue-700/10 scale-0 group-hover:scale-150 transition-transform duration-500 ease-out"></span>
                    <FaLinkedin size={20} className="relative z-10" />
                  </a>
                  
                  {/* Bouton TikTok */}
                  <a href="https://www.tiktok.com/@forfeoinc" target="_blank" rel="noopener noreferrer" 
                     className="relative flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 text-navy border border-slate-200 shadow-sm hover:shadow-md hover:border-black hover:text-black transition-all duration-300 hover:-translate-y-1 group overflow-hidden">
                    <span className="absolute inset-0 rounded-full bg-black/10 scale-0 group-hover:scale-150 transition-transform duration-500 ease-out"></span>
                    <FaTiktok size={20} className="relative z-10" />
                  </a>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-navy font-black text-xs uppercase tracking-widest mb-6">Navigation</h4>
              <ul className="space-y-4 text-sm font-bold">
                <li>
                  <Link to="/" onClick={scrollToTop} className="text-slate-500 hover:text-blue-600 transition-colors">
                    Accueil
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => setIsForfeoComRedirectOpen(true)}
                    className="text-blue-600 hover:text-navy transition-colors flex items-center gap-2 text-left"
                  >
                    <FaExternalLinkAlt /> Accéder à la boutique Forfeo
                  </button>
                </li>
                <li>
                  <a
                    href="https://forfeoexplore.com/login#portail-commercant"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 transition-colors flex items-center gap-2"
                  >
                    <FaPlusCircle /> Inscrire mon entreprise
                  </a>
                </li>
                <li>
                  <button
                    onClick={() => setIsContactOpen(true)}
                    className="text-slate-500 hover:text-blue-600 transition-colors flex items-center gap-2"
                  >
                    <FaEnvelope /> Contactez-nous
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-navy font-black text-xs uppercase tracking-widest mb-6">Légal</h4>
              <ul className="space-y-4 text-sm font-bold">
                <li>
                  <Link to="/privacy" onClick={scrollToTop} className="text-slate-500 hover:text-blue-600 transition-colors">
                    Confidentialité
                  </Link>
                </li>
                <li>
                  <Link to="/terms" onClick={scrollToTop} className="text-slate-500 hover:text-blue-600 transition-colors">
                    Conditions d&apos;utilisation
                  </Link>
                </li>
                <li className="pt-2">
                  <span className="inline-block bg-green-50 text-green-700 border border-green-200 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter">
                    ✓ Conformité Loi 25 (Québec)
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mb-12">
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-sm">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-5 relative min-h-[240px] sm:min-h-[280px] lg:min-h-[320px]">
                  <img
                    src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=1200&auto=format&fit=crop"
                    alt="Infolettre Forfeo"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                  <div className="absolute inset-0 bg-navy/15" />
                </div>

                <div className="lg:col-span-7 bg-[#d8d4ca] px-6 md:px-8 py-8">
                  <h3 className="text-2xl md:text-3xl leading-tight text-slate-800 font-light mb-4">
                    Profitez de nos offres avant tout le monde, abonnez-vous à l’infolettre Forfeo!
                  </h3>

                  <p className="text-slate-700 mb-3 font-medium">L’infolettre Forfeo c’est :</p>
                  <ul className="list-disc pl-5 text-slate-700 space-y-1 text-sm md:text-base">
                    <li>Des promotions exclusives chaque semaine</li>
                    <li>Des offres last call et découvertes locales</li>
                    <li>Des lancements en avant-première</li>
                    <li>Des surprises et avantages réservés aux abonnés</li>
                  </ul>
                </div>
              </div>

              <form onSubmit={handleNewsletterSubmit} className="grid grid-cols-1 md:grid-cols-12 border-t border-slate-200 bg-white">
        <input
          type="text"
          name="website"
          value={publicEmailWebsite}
          onChange={(event) =>
            setPublicEmailWebsite(
              event.target.value,
            )
          }
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            opacity: 0,
          }}
        />

                <div className="md:col-span-4 relative border-b md:border-b-0 md:border-r border-slate-200">
                  <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Courriel *"
                    className="w-full h-16 pl-12 pr-4 outline-none text-slate-700 placeholder:text-slate-400 font-bold"
                    required
                  />
                </div>

                <div className="md:col-span-4 relative border-b md:border-b-0 md:border-r border-slate-200">
                  <FaMapMarkedAlt className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={newsletterPostalCode}
                    onChange={(e) => setNewsletterPostalCode(normalizePostalCode(e.target.value))}
                    placeholder="Code postal *"
                    maxLength={7}
                    className="w-full h-16 pl-12 pr-4 outline-none text-slate-700 placeholder:text-slate-400 uppercase font-bold"
                    required
                  />
                </div>

                <div className="md:col-span-4">
                  <button
                    type="submit"
                    disabled={newsletterLoading}
                    className="w-full h-16 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-black transition uppercase tracking-widest text-sm"
                  >
                    {newsletterLoading ? 'Envoi...' : "Je m'abonne"}
                  </button>
                </div>
              </form>

              {newsletterMessage && (
                <div
                  className={`px-5 py-3 text-sm font-medium border-t ${
                    newsletterMessage.type === 'success'
                      ? 'bg-green-50 text-green-700 border-green-100'
                      : 'bg-red-50 text-red-700 border-red-100'
                  }`}
                >
                  {newsletterMessage.text}
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-slate-100 pt-10 text-center">
            <p className="text-xs text-slate-400 font-medium">
              Forfeo Explore Québec est une division exploitée par <strong className="text-navy font-bold">FORFEO INC.</strong>, société par actions enregistrée au Québec.
            </p>
            <p className="mt-3 text-[10px] text-slate-300 font-black uppercase tracking-widest italic">
              © {currentYear} Forfeo Inc. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>

      {/* --- MODAL DE CONTACT --- */}
      {isContactOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-gradient-to-br from-white to-slate-100 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 animate-slide-in-up">
            <div className="bg-white/50 border-b border-slate-100 p-5 flex justify-between items-center backdrop-blur-sm">
              <h2 className="text-2xl font-black text-navy uppercase tracking-tighter flex items-center gap-3">
                <FaEnvelope className="text-blue-600 bg-blue-50 p-2 rounded-lg" size={40}/> Contactez-nous
              </h2>
              <button
                onClick={() => setIsContactOpen(false)}
                className="text-slate-400 hover:text-red-500 transition-colors p-3 bg-slate-50 hover:bg-red-50 rounded-full"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              {contactStatus === 'success' ? (
                <div className="bg-green-50 text-green-700 border border-green-200 rounded-xl p-6 text-center animate-in zoom-in-95">
                  <FaEnvelope className="text-green-600 bg-white p-3 rounded-full shadow-sm mx-auto mb-4" size={50}/>
                  <p className="font-bold text-lg mb-3">Message Envoyé !</p>
                  <p className="text-sm leading-relaxed mb-5">{contactMessage}</p>
                  
                  {/* --- SECTION: GRILLE DE 6 LIENS --- */}
                  <div className="border-t border-green-200 pt-5 space-y-4">
                    <p className="text-xs font-bold text-green-700 uppercase tracking-widest">En attendant une réponse...</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      
                      {/* 1. Service Client Intelligent */}
                      <a href="https://forfeoexplore.com/service-client" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all border border-green-100 flex flex-col items-center text-center gap-1 group text-green-700">
                        <div className="flex items-center gap-2">
                          <FaExternalLinkAlt size={12} className="group-hover:text-blue-600 transition"/>
                          <span className="font-bold text-sm">Service Client IA</span>
                        </div>
                        <span className="text-[10px] leading-tight opacity-80 mt-1">Contactez l'assistance intelligente</span>
                      </a>
                      
                      {/* 2. Boutique Forfeo */}
                      <a href="https://forfeoexplore.com/boutique" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all border border-green-100 flex flex-col items-center text-center gap-1 group text-green-700">
                        <div className="flex items-center gap-2">
                          <FaExternalLinkAlt size={12} className="group-hover:text-blue-600 transition"/>
                          <span className="font-bold text-sm">Boutique Forfeo</span>
                        </div>
                        <span className="text-[10px] leading-tight opacity-80 mt-1">Soutenez l'économie locale. Découvrez nos forfaits exclusifs.</span>
                      </a>

                      {/* 3. Marketplace Cartes-Cadeaux */}
                      <button type="button" onClick={() => setIsForfeoComRedirectOpen(true)} className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all border border-green-100 flex flex-col items-center text-center gap-1 group text-green-700">
                        <div className="flex items-center gap-2">
                          <FaExternalLinkAlt size={12} className="group-hover:text-blue-600 transition"/>
                          <span className="font-bold text-sm">Marketplace</span>
                        </div>
                        <span className="text-[10px] leading-tight opacity-80 mt-1">Achat de cartes-cadeaux sur forfeo.com.</span>
                      </button>

                      {/* 4. Réseau Local */}
                      <a href="https://forfeoexplore.com/reseau-local" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all border border-green-100 flex flex-col items-center text-center gap-1 group text-green-700">
                        <div className="flex items-center gap-2">
                          <FaExternalLinkAlt size={12} className="group-hover:text-blue-600 transition"/>
                          <span className="font-bold text-sm">Réseau Local</span>
                        </div>
                        <span className="text-[10px] leading-tight opacity-80 mt-1">Soutenez nos commerçants</span>
                      </a>

                      {/* 5. Last Call */}
                      <a href="https://forfeoexplore.com/last-call" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all border border-green-100 flex flex-col items-center text-center gap-1 group text-green-700">
                        <div className="flex items-center gap-2">
                          <FaExternalLinkAlt size={12} className="group-hover:text-blue-600 transition"/>
                          <span className="font-bold text-sm">Offres Last Call</span>
                        </div>
                        <span className="text-[10px] leading-tight opacity-80 mt-1">Aubaines de dernière minute</span>
                      </a>

                      {/* 6. Forfeo Drops */}
                      <a href="https://forfeoexplore.com/drops" target="_blank" rel="noopener noreferrer" className="bg-white p-3 rounded-lg shadow-sm hover:shadow-md transition-all border border-green-100 flex flex-col items-center text-center gap-1 group text-green-700">
                        <div className="flex items-center gap-2">
                          <FaExternalLinkAlt size={12} className="group-hover:text-blue-600 transition"/>
                          <span className="font-bold text-sm">Forfeo Drops</span>
                        </div>
                        <span className="text-[10px] leading-tight opacity-80 mt-1">Déplace-toi pour débloquer des récompenses locales.</span>
                      </a>
                      
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-5 animate-in fade-in zoom-in-95">
        <input
          type="text"
          name="website"
          value={publicEmailWebsite}
          onChange={(event) =>
            setPublicEmailWebsite(
              event.target.value,
            )
          }
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            opacity: 0,
          }}
        />

                  {contactStatus === 'error' && (
                    <div className="bg-red-50 text-red-700 p-4 rounded-xl text-sm border border-red-100 font-medium">
                      {contactMessage}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="relative">
                      <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        required
                        value={contactData.name}
                        onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                        className="w-full p-4 pl-12 border border-slate-200 rounded-lg outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition font-medium text-slate-700 placeholder:text-slate-400"
                        placeholder="Jean Tremblay *"
                      />
                    </div>
                    <div className="relative">
                      <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="email"
                        required
                        value={contactData.email}
                        onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                        className="w-full p-4 pl-12 border border-slate-200 rounded-lg outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition font-medium text-slate-700 placeholder:text-slate-400"
                        placeholder="jean@exemple.com *"
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <FaTag className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      required
                      value={contactData.subject}
                      onChange={(e) => setContactData({ ...contactData, subject: e.target.value })}
                      className="w-full p-4 pl-12 border border-slate-200 rounded-lg outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition font-medium text-slate-700 placeholder:text-slate-400"
                      placeholder="Sujet de votre message *"
                    />
                  </div>

                  <div className="relative">
                    <FaComment className="absolute left-4 top-5 text-slate-400" />
                    <textarea
                      required
                      rows={5}
                      value={contactData.message}
                      onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                      className="w-full p-4 pl-12 pt-5 border border-slate-200 rounded-lg outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition resize-none font-medium text-slate-700 placeholder:text-slate-400"
                      placeholder="Comment pouvons-nous vous aider ? *"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={contactStatus === 'loading'}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest py-5 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed text-sm shadow-md hover:shadow-lg hover:scale-[1.01]"
                  >
                    {contactStatus === 'loading' ? 'Envoi en cours...' : 'Envoyer le message'}
                  </button>
                </form>
              )}
            </div>
            {contactStatus !== 'success' && (
                <div className="border-t border-slate-100 bg-slate-50 p-4 text-center text-xs text-slate-400">
                    Notre équipe est dédiée à votre expérience. Tous les champs marqués d'un * sont requis.
                </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
